<template>
  <modal
    :open="true"
    name="simulate-query"
    height="auto"
    width="1060"
    :lazy="true"
    :scrollable="true"
    :clickToClose="false"
    @opened="onOpened"
    @closed="onClosed"
  >
    <div class="rc-card">
      <!-- close -->
      <button
        type="button"
        class="rc-close"
        aria-label="Fechar"
        @click="closeModal"
      >
        <svg
          width="13"
          height="13"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="3"
          stroke-linecap="round"
        >
          <path d="M6 6l12 12M18 6L6 18" />
        </svg>
      </button>

      <!-- header -->
      <div class="rc-header">
        <div class="rc-eyebrow">Resultado da consulta</div>
        <div class="rc-header-row">
          <div class="rc-title-group">
            <h1 class="rc-title">{{ viewName }}</h1>
            <span v-if="isFetching" class="rc-pill rc-pill--neutral">
              <span class="rc-dot rc-dot--neutral"></span>Executando
            </span>
            <span v-else-if="errorMessage" class="rc-pill rc-pill--error">
              <span class="rc-dot rc-dot--error"></span>Falha
            </span>
            <span v-else class="rc-pill rc-pill--ok">
              <span class="rc-dot rc-dot--ok"></span>Carga OK
            </span>
          </div>
          <div v-if="!isFetching && !errorMessage" class="rc-stats">
            <div class="rc-stat">
              <b>{{ totalRegistros }}</b> registros
            </div>
            <div class="rc-stat">
              <b>{{ numColunas }}</b> colunas
            </div>
            <div v-if="execTimeText" class="rc-stat rc-stat--time">
              {{ execTimeText }}
            </div>
          </div>
        </div>
      </div>

      <!-- toolbar -->
      <div
        v-if="!isFetching && !errorMessage && result.itens.length"
        class="rc-toolbar"
      >
        <div class="rc-search">
          <svg
            class="rc-search-icon"
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2.2"
            stroke-linecap="round"
          >
            <circle cx="11" cy="11" r="7" />
            <path d="M20 20l-3.2-3.2" />
          </svg>
          <input
            v-model="query"
            class="rc-input"
            type="text"
            placeholder="Filtrar nos resultados..."
          />
        </div>
        <div class="rc-hint">
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2.4"
            stroke-linecap="round"
          >
            <path d="M9 6L4 12l5 6M15 6l5 6-5 6" />
          </svg>
          Role para ver mais colunas
        </div>
      </div>

      <!-- body -->
      <div class="rc-body">
        <!-- loading -->
        <div v-if="isFetching" class="rc-state">
          <LoadingMessage message="Carregando, aguarde" is-active align-center />
        </div>

        <!-- error -->
        <div v-else-if="errorMessage" class="rc-error">
          <div class="rc-error-head">
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M12 9v4M12 17h.01" />
              <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" />
            </svg>
            Falha ao executar a consulta
          </div>
          <pre class="rc-error-msg">{{ errorMessage }}</pre>
        </div>

        <!-- empty -->
        <div v-else-if="!filteredRows.length" class="rc-state rc-state--empty">
          Nenhum registro corresponde ao filtro.
        </div>

        <!-- table -->
        <div v-else class="rc-scroll">
          <table class="rc-table">
            <thead>
              <tr>
                <th v-for="col in columns" :key="col">{{ col }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, i) in filteredRows" :key="i">
                <td v-for="col in columns" :key="col">
                  <span v-if="isNullVal(row[col])" class="rc-null">NULL</span>
                  <span v-else :class="{ 'rc-mono': isNumericLike(row[col]) }">{{ row[col] }}</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- footer -->
      <div class="rc-footer">
        <div class="rc-footer-left">
          <template v-if="!isFetching && !errorMessage && result.itens.length">
            <span class="rc-showing">{{ showingText }}</span>
            <div v-if="lastPage > 1" class="rc-pages">
              <button
                type="button"
                class="rc-nav"
                :disabled="currentPage <= 1"
                aria-label="Anterior"
                @click="goToPage(currentPage - 1)"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round">
                  <path d="M15 6l-6 6 6 6" />
                </svg>
              </button>
              <button
                v-for="p in pages"
                :key="p"
                type="button"
                class="rc-page"
                :class="{ 'rc-page--active': p === currentPage }"
                @click="goToPage(p)"
              >
                {{ p }}
              </button>
              <button
                type="button"
                class="rc-nav"
                :disabled="currentPage >= lastPage"
                aria-label="Próxima"
                @click="goToPage(currentPage + 1)"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round">
                  <path d="M9 6l6 6-6 6" />
                </svg>
              </button>
            </div>
          </template>
        </div>
        <div class="rc-actions">
          <button type="button" class="rc-btn rc-btn--primary" @click="executeQuery(currentPage)">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round">
              <path d="M21 12a9 9 0 1 1-2.64-6.36M21 3v5h-5" />
            </svg>
            Reexecutar
          </button>
          <button type="button" class="rc-btn rc-btn--ghost" @click="closeModal">
            Fechar
          </button>
        </div>
      </div>
    </div>
  </modal>
</template>

<script>
import { post } from "@/helpers/apiRequest";
import { tokenize } from "@/helpers/sql/sqlRiverFormatter";

export default {
  name: "ModalSimulateQuery",
  props: {
    // SQL a executar/simular (setado pela tela antes de abrir o modal).
    sql: { type: String, default: "" },
  },
  data() {
    return {
      errorMessage: null,
      isFetching: false,
      result: {
        itens: [],
        paginator: {},
      },
      columns: [],
      query: "",
      execTime: null,
    };
  },
  computed: {
    // Nome exibido no título: primeiro identificador após o primeiro FROM,
    // via tokenizer (regex simples casava FROM dentro de comentário/string).
    viewName() {
      const tokens = tokenize(this.sql || "");
      const idx = tokens.findIndex(
        (t) => t.type === "word" && t.value.toUpperCase() === "FROM"
      );
      const alvo = idx !== -1 && tokens[idx + 1];
      if (!alvo || alvo.type !== "word") return "CONSULTA";

      // inclui o qualificador (schema.tabela)
      let nome = alvo.value;
      let j = idx + 2;
      while (
        tokens[j] && tokens[j].type === "dot" &&
        tokens[j + 1] && tokens[j + 1].type === "word"
      ) {
        nome += "." + tokens[j + 1].value;
        j += 2;
      }
      return nome.replace(/"/g, "").toUpperCase();
    },
    totalRegistros() {
      return this.result.paginator.total != null
        ? this.result.paginator.total
        : this.result.itens.length;
    },
    numColunas() {
      return this.columns.length;
    },
    execTimeText() {
      if (this.execTime == null) return null;
      return (this.execTime / 1000).toFixed(2).replace(".", ",") + " s";
    },
    currentPage() {
      return this.result.paginator.current_page || 1;
    },
    lastPage() {
      return this.result.paginator.last_page || 1;
    },
    pages() {
      let start = Math.max(1, this.currentPage - 2);
      let end = Math.min(this.lastPage, start + 4);
      start = Math.max(1, end - 4);
      const list = [];
      for (let n = start; n <= end; n++) list.push(n);
      return list;
    },
    filteredRows() {
      const q = this.query.trim().toLowerCase();
      if (!q) return this.result.itens;
      return this.result.itens.filter((row) =>
        this.columns.some((col) => {
          const value = row[col];
          return value != null && String(value).toLowerCase().includes(q);
        })
      );
    },
    showingText() {
      const q = this.query.trim();
      if (q) {
        return `${this.filteredRows.length} de ${this.result.itens.length} nesta página`;
      }
      const p = this.result.paginator;
      if (!this.totalRegistros) return "Nenhum registro";
      const from = p.from != null ? p.from : 1;
      const to = p.to != null ? p.to : this.result.itens.length;
      return `Mostrando ${from}–${to} de ${this.totalRegistros} registros`;
    },
  },
  watch: {
    sql: function () {
      if (this.sql) {
        // Nova consulta: só aqui o filtro é zerado. Na paginação/reexecução da
        // mesma consulta ele persiste (ver executeQuery).
        this.query = "";
        this.executeQuery(1);
      }
    },
  },
  beforeDestroy() {
    // Redundância defensiva: se o componente morrer com o modal aberto.
    document.removeEventListener("keyup", this.escListener);
  },
  methods: {
    // O Esc só é observado enquanto o modal está aberto (eventos do vue-js-modal),
    // em vez de um listener global permanente durante toda a vida da tela.
    onOpened() {
      document.addEventListener("keyup", this.escListener);
    },
    onClosed() {
      document.removeEventListener("keyup", this.escListener);
    },
    executeQuery(page = 1) {
      this.isFetching = true;
      const startedAt = performance.now();
      post(`/admin/sqls-erp/simulate?page=${page}`, { sql: this.sql })
        .then((json) => {
          this.execTime = performance.now() - startedAt;
          this.isFetching = false;
          this.errorMessage = null;
          this.result = json;
          this.columns = json.itens.length ? Object.keys(json.itens[0]) : [];
        })
        .catch((e) => {
          this.isFetching = false;
          this.errorMessage = e.message;
        });
    },
    isNullVal(value) {
      return value === null || value === undefined;
    },
    isNumericLike(value) {
      return /^[\d.,/\s-]+$/.test(String(value).trim()) && String(value).trim() !== "";
    },
    goToPage(page) {
      if (page < 1 || page > this.lastPage || page === this.currentPage) return;
      this.executeQuery(page);
    },
    escListener(e) {
      if (e.key === "Escape") {
        this.closeModal();
      }
    },
    closeModal() {
      this.$modal.hide("simulate-query");
    },
  },
};
</script>

<style scoped>
.rc-card {
  --primary: #33366e;
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-height: 88vh;
  background: #fff;
  border-radius: 12px;
  font-family: inherit;
}

/* close */
.rc-close {
  position: absolute;
  top: 12px;
  right: 12px;
  width: 34px;
  height: 34px;
  border: none;
  border-radius: 50%;
  background: #f0454b;
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 5px 14px rgba(240, 69, 75, 0.45);
  z-index: 5;
}
.rc-close:hover {
  background: #dc3940;
}

/* header */
.rc-header {
  padding: 22px 56px 16px 26px;
  border-bottom: 1px solid #ecedf2;
  border-radius: 12px 12px 0 0;
}
.rc-eyebrow {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.13em;
  color: #9396a8;
  text-transform: uppercase;
}
.rc-header-row {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 20px;
  margin-top: 5px;
  flex-wrap: wrap;
}
.rc-title-group {
  display: flex;
  align-items: center;
  gap: 11px;
}
.rc-title {
  margin: 0;
  font-size: 21px;
  font-weight: 800;
  color: #2c2e63;
  font-family: SFMono-Regular, Menlo, Consolas, monospace;
  letter-spacing: -0.01em;
}
.rc-pill {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 9px;
  border-radius: 999px;
  font-size: 11.5px;
  font-weight: 700;
}
.rc-pill--ok {
  background: #e5f6ec;
  color: #198f52;
}
.rc-pill--error {
  background: #fdecee;
  color: #d23644;
}
.rc-pill--neutral {
  background: #eef0f5;
  color: #5b6072;
}
.rc-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
}
.rc-dot--ok {
  background: #22b36b;
}
.rc-dot--error {
  background: #e0414f;
}
.rc-dot--neutral {
  background: #9aa0b2;
}
.rc-stats {
  display: flex;
  align-items: center;
  gap: 8px;
}
.rc-stat {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 5px 11px;
  border-radius: 8px;
  background: #f3f4f8;
  color: #5b6072;
  font-size: 12.5px;
  font-weight: 600;
}
.rc-stat b {
  color: #2c2e63;
  font-weight: 800;
}
.rc-stat--time {
  background: #eaf6f6;
  color: #138184;
  font-family: SFMono-Regular, Menlo, Consolas, monospace;
}

/* toolbar */
.rc-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 13px 26px;
  background: #fafbfc;
  border-bottom: 1px solid #ecedf2;
  flex-wrap: wrap;
}
.rc-search {
  position: relative;
  flex: 1;
  min-width: 220px;
  max-width: 360px;
}
.rc-search-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #a2a7b5;
}
.rc-input {
  width: 100%;
  padding: 9px 12px 9px 34px;
  border: 1px solid #e1e3ea;
  border-radius: 9px;
  font-size: 13px;
  font-family: inherit;
  color: #2c2e63;
  outline: none;
  background: #fff;
}
.rc-input::placeholder {
  color: #a2a7b5;
}
.rc-input:focus {
  border-color: var(--primary);
  box-shadow: 0 0 0 3px rgba(51, 54, 110, 0.1);
}
.rc-hint {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #9ca1ae;
  font-size: 12px;
  font-weight: 600;
}

/* body */
.rc-body {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  background: #fff;
}
.rc-state {
  padding: 54px 20px;
  text-align: center;
  color: #9ca1ae;
  font-size: 14px;
}

/* error */
.rc-error {
  margin: 20px 26px;
  border: 1px solid #f3c5cb;
  border-radius: 10px;
  background: #fdf3f4;
  overflow: hidden;
}
.rc-error-head {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: #fceaec;
  color: #c22f3d;
  font-size: 13px;
  font-weight: 700;
  border-bottom: 1px solid #f3c5cb;
}
.rc-error-msg {
  margin: 0;
  padding: 14px 16px;
  font-family: SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 12.5px;
  color: #7a2630;
  white-space: pre-wrap;
  word-break: break-word;
}

/* table */
.rc-scroll {
  flex: 1;
  min-height: 0;
  overflow: auto;
  background: #fff;
}
.rc-scroll::-webkit-scrollbar {
  height: 11px;
  width: 11px;
}
.rc-scroll::-webkit-scrollbar-track {
  background: #f3f4f7;
}
.rc-scroll::-webkit-scrollbar-thumb {
  background: #c7cbd6;
  border-radius: 6px;
  border: 2px solid #f3f4f7;
}
.rc-scroll::-webkit-scrollbar-thumb:hover {
  background: #aeb3c2;
}
.rc-table {
  width: 100%;
  min-width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 13px;
}
.rc-table thead th {
  position: sticky;
  top: 0;
  z-index: 2;
  padding: 11px 14px;
  text-align: left;
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: #8a90a2;
  white-space: nowrap;
  background: #f7f8fa;
  box-shadow: inset 0 -1px 0 #e7e9ee;
}
.rc-table tbody td {
  padding: 13px 14px;
  color: #3a3f4b;
  white-space: nowrap;
  border-bottom: 1px solid #eff1f4;
  vertical-align: middle;
}
.rc-table tbody tr:hover td {
  background: #f6f9fb;
}
.rc-null {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 5px;
  background: #f1f2f5;
  color: #a6abba;
  font-family: SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 11px;
  font-style: italic;
}
.rc-mono {
  font-family: SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 12.5px;
}

/* footer */
.rc-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  padding: 15px 26px;
  border-top: 1px solid #ecedf2;
  background: #fff;
  border-radius: 0 0 12px 12px;
  flex-wrap: wrap;
}
.rc-footer-left {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
  min-height: 32px;
}
.rc-showing {
  font-size: 12.5px;
  color: #8a90a2;
  font-weight: 500;
}
.rc-pages {
  display: flex;
  align-items: center;
  gap: 6px;
}
.rc-page,
.rc-nav {
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  font-family: inherit;
  cursor: pointer;
  transition: all 0.12s;
}
.rc-page {
  font-size: 13px;
  font-weight: 700;
  background: #fff;
  color: #4a4f5e;
  border: 1px solid #e1e3ea;
}
.rc-page--active {
  background: var(--primary);
  color: #fff;
  border: 1px solid var(--primary);
}
.rc-nav {
  background: #fff;
  color: #5b6072;
  border: 1px solid #e1e3ea;
}
.rc-nav:disabled {
  opacity: 0.4;
  pointer-events: none;
}
.rc-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}
.rc-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border-radius: 9px;
  font-size: 13.5px;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
}
.rc-btn--primary {
  padding: 10px 18px;
  border: none;
  background: var(--primary);
  color: #fff;
  box-shadow: 0 4px 12px rgba(44, 46, 99, 0.28);
}
.rc-btn--primary:hover {
  filter: brightness(1.08);
}
.rc-btn--ghost {
  padding: 10px 20px;
  border: 1px solid #d8dae2;
  background: #fff;
  color: #4a4f5e;
}
.rc-btn--ghost:hover {
  background: #f5f6f8;
  border-color: #c7cbd6;
}
</style>
